import { mapInviteError } from "@/api/invitations/mapInviteError";
import { supabase } from "@/api/supabaseClient";
import {
  formatOrgRoles,
  isOrgMembershipRole,
  type OrgMembershipRole,
} from "@/auth/membershipRoles";
import type { Database } from "@/types/database";
import type { SupabaseClient } from "@supabase/supabase-js";

type Db = SupabaseClient<Database>;
type InvitationRow = Database["public"]["Tables"]["organization_invitations"]["Row"];

const db = supabase as Db;

export type InvitationStatus = Database["public"]["Enums"]["invitation_status"];

export type OrganizationInvitation = {
  id: string;
  organization_id: string;
  email: string;
  display_name: string;
  roles: OrgMembershipRole[];
  status: InvitationStatus;
  expires_at: string;
  created_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
};

export type InvitePreview = {
  organizationName: string;
  displayName: string;
  email: string;
  roles: OrgMembershipRole[];
  rolesLabel: string;
  status: InvitationStatus | string;
  expiresAt: string;
  accountExists: boolean;
};

export type AcceptInviteResult = {
  mode: "new_user" | "existing_user";
  membershipId: string | null;
  session: { access_token: string; refresh_token: string } | null;
};

async function inviteCallError(
  scope: string,
  error: unknown,
  action?: "invite" | "resend" | "revoke",
): Promise<Error> {
  const parsed = await readFunctionFailure(error);
  console.error(scope, parsed.status, parsed.message);
  return new Error(mapInviteError({ ...parsed, action }));
}

async function readFunctionFailure(
  error: unknown,
): Promise<{ status: number | null; message: string | null }> {
  let status: number | null = null;
  let message: string | null = null;
  if (error && typeof error === "object") {
    const row = error as {
      message?: unknown;
      context?: { status?: number; json?: () => Promise<{ error?: string }> };
    };
    if (typeof row.context?.status === "number") status = row.context.status;
    if (typeof row.context?.json === "function") {
      try {
        const body = await row.context.json();
        if (typeof body?.error === "string") message = body.error;
      } catch {
        // Body nicht lesbar
      }
    }
    if (!message && typeof row.message === "string") message = row.message;
  }
  return { status, message };
}

async function functionErrorMessage(error: unknown, fallback: string): Promise<string> {
  const ctx = (error as { context?: { json?: () => Promise<{ error?: string }> } }).context;
  if (ctx && typeof ctx.json === "function") {
    try {
      const body = await ctx.json();
      if (body?.error) return body.error;
    } catch {
      // Body nicht lesbar
    }
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

function mapInvitation(row: InvitationRow): OrganizationInvitation {
  const roles = Array.isArray(row.roles)
    ? row.roles.filter((role): role is OrgMembershipRole => isOrgMembershipRole(role))
    : [];
  return {
    id: String(row.id),
    organization_id: String(row.organization_id),
    email: String(row.email),
    display_name: String(row.display_name),
    roles,
    status: row.status,
    expires_at: String(row.expires_at),
    created_at: String(row.created_at),
    accepted_at: row.accepted_at,
    revoked_at: row.revoked_at,
  };
}

export class InvitationsApi {
  static async amISuperadmin(): Promise<boolean> {
    const { data: userData } = await supabase.auth.getUser();
    const id = userData.user?.id;
    if (!id) return false;
    const { data, error } = await db
      .from("profiles")
      .select("is_superadmin")
      .eq("id", id)
      .maybeSingle();
    if (error) throw error;
    return Boolean(data?.is_superadmin);
  }

  static async listForOrganization(organizationId: string): Promise<OrganizationInvitation[]> {
    const { data, error } = await db
      .from("organization_invitations")
      .select("id, organization_id, email, display_name, roles, status, expires_at, created_at, accepted_at, revoked_at")
      .eq("organization_id", organizationId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return (data ?? []).map(mapInvitation);
  }

  static async invite(input: {
    organizationId: string;
    email: string;
    displayName: string;
    roles: OrgMembershipRole[];
  }): Promise<void> {
    const { error } = await supabase.functions.invoke("invite-member", {
      body: {
        organizationId: input.organizationId,
        email: input.email.trim().toLowerCase(),
        displayName: input.displayName.trim(),
        roles: input.roles,
      },
    });
    if (error) throw await inviteCallError("invite-member", error);
  }

  static async resend(invitationId: string): Promise<void> {
    const { error } = await supabase.functions.invoke("resend-invite", {
      body: { invitationId },
    });
    if (error) throw await inviteCallError("resend-invite", error, "resend");
  }

  static async revoke(invitationId: string): Promise<void> {
    const { error } = await supabase.functions.invoke("revoke-invite", {
      body: { invitationId },
    });
    if (error) {
      const parsed = await readFunctionFailure(error);
      console.error("revoke-invite", parsed.status, parsed.message);
      throw new Error(mapInviteError({ ...parsed, action: "revoke" }));
    }
  }

  static async preview(token: string): Promise<InvitePreview> {
    const { data, error } = await supabase.functions.invoke("preview-invite", {
      body: { token },
    });
    if (error) throw new Error(await functionErrorMessage(error, "Einladung ungültig."));
    const row = (data ?? {}) as Record<string, unknown>;
    const roles = Array.isArray(row.roles)
      ? row.roles.filter((role): role is OrgMembershipRole => typeof role === "string" && isOrgMembershipRole(role))
      : [];
    return {
      organizationName: String(row.organizationName ?? ""),
      displayName: String(row.displayName ?? ""),
      email: String(row.email ?? ""),
      roles,
      rolesLabel: formatOrgRoles(roles),
      status: String(row.status ?? ""),
      expiresAt: String(row.expiresAt ?? ""),
      accountExists: Boolean(row.accountExists),
    };
  }

  static async accept(input: {
    token: string;
    displayName: string;
    password?: string;
  }): Promise<AcceptInviteResult> {
    const { data, error } = await supabase.functions.invoke("accept-invite", {
      body: {
        token: input.token,
        displayName: input.displayName.trim(),
        password: input.password,
      },
    });
    if (error) throw new Error(await functionErrorMessage(error, "Einladung konnte nicht angenommen werden."));
    const row = (data ?? {}) as Record<string, unknown>;
    const session = row.session as { access_token?: string; refresh_token?: string } | null;
    return {
      mode: row.mode === "existing_user" ? "existing_user" : "new_user",
      membershipId: typeof row.membershipId === "string" ? row.membershipId : null,
      session:
        session?.access_token && session.refresh_token
          ? { access_token: session.access_token, refresh_token: session.refresh_token }
          : null,
    };
  }
}
